import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Loader2, Plus, UserPlus, Users, Tag as TagIcon, Filter } from "lucide-react";
import { AddClientDialog } from "@/components/AddClientDialog";

const AdminCodes = () => {
    const [codes, setCodes] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [addClientOpen, setAddClientOpen] = useState(false);
    const [tagFilter, setTagFilter] = useState("");
    const [form, setForm] = useState({
        label: "",
        tag: "",
        max_uses: 25,
        expires_at: "",
        monthly_script_limit: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const [c, t] = await Promise.all([
                api.get("/admin/codes"),
                api.get("/admin/tags"),
            ]);
            setCodes(c.data);
            setTags(t.data);
        } catch {
            toast.error("Failed to load codes");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const body = {
                label: form.label || null,
                tag: form.tag || null,
                max_uses: parseInt(form.max_uses, 10) || 1,
                expires_at: form.expires_at || null,
                monthly_script_limit: form.monthly_script_limit ? parseInt(form.monthly_script_limit, 10) : null,
            };
            const { data } = await api.post("/admin/codes", body);
            toast.success(`Code created: ${data.code}`);
            setForm({ label: "", tag: "", max_uses: 25, expires_at: "", monthly_script_limit: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create code");
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (id) => {
        if (!window.confirm("Revoke this activation code? Anyone who already redeemed keeps their access.")) return;
        try {
            await api.post(`/admin/codes/${id}/revoke`);
            toast.success("Code revoked");
            load();
        } catch {
            toast.error("Revoke failed");
        }
    };

    const copyCode = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            toast.success("Code copied");
        } catch {
            toast.error("Copy failed");
        }
    };

    const filteredCodes = useMemo(() => {
        if (!tagFilter) return codes;
        return codes.filter((c) => (c.tag || "") === tagFilter);
    }, [codes, tagFilter]);

    const daysLeft = (iso) => {
        if (!iso) return null;
        const ms = new Date(iso).getTime() - Date.now();
        return Math.ceil(ms / (1000 * 60 * 60 * 24));
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="admin-codes">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
                <div>
                    <div className="un-label text-un-grape mb-3">Admin · Codes</div>
                    <h1 className="un-h1 text-5xl md:text-6xl">
                        Activation <span className="text-un-berry">Codes</span>.
                    </h1>
                    <p className="text-lg text-un-onyx/70 mt-3 max-w-2xl">
                        Generate one shared code for a batch of clients, or add a client one at a time.
                    </p>
                </div>
                <button
                    onClick={() => setAddClientOpen(true)}
                    className="un-btn-outline text-sm flex items-center gap-2"
                    data-testid="add-client-button"
                >
                    <UserPlus className="w-4 h-4" /> Add One Client
                </button>
            </div>

            <form
                onSubmit={create}
                className="un-card p-6 mb-10 grid grid-cols-1 md:grid-cols-4 gap-4 items-end"
                data-testid="new-code-form"
            >
                <label className="block md:col-span-2">
                    <span className="un-label text-un-onyx block mb-2">Label (Optional)</span>
                    <input
                        type="text"
                        value={form.label}
                        onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                        className="un-input"
                        placeholder="e.g. Q1 Launch Cohort"
                        data-testid="code-label-input"
                    />
                </label>
                <label className="block">
                    <span className="un-label text-un-onyx block mb-2 flex items-center gap-1">
                        <TagIcon className="w-3 h-3" /> Tag (Optional)
                    </span>
                    <input
                        type="text"
                        list="tag-suggestions"
                        value={form.tag}
                        onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}
                        className="un-input"
                        placeholder="Pick or type new"
                        data-testid="code-tag-input"
                    />
                    <datalist id="tag-suggestions">
                        {tags.map((t) => (
                            <option key={t.id} value={t.name} />
                        ))}
                    </datalist>
                </label>
                <label className="block">
                    <span className="un-label text-un-onyx block mb-2 flex items-center gap-1">
                        <Users className="w-3 h-3" /> Max Uses
                    </span>
                    <input
                        type="number"
                        min="1"
                        value={form.max_uses}
                        onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
                        className="un-input"
                        data-testid="code-max-uses-input"
                    />
                </label>
                <label className="block md:col-span-2">
                    <span className="un-label text-un-onyx block mb-2">Access Expires On</span>
                    <span className="text-xs text-un-onyx/60 mb-2 block">
                        All clients who redeem this code lose access on this date.
                    </span>
                    <input
                        type="date"
                        value={form.expires_at}
                        onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                        className="un-input"
                        data-testid="code-expires-input"
                        min={new Date().toISOString().split("T")[0]}
                    />
                </label>
                <label className="block md:col-span-2">
                    <span className="un-label text-un-onyx block mb-2">Monthly Generation Limit (Per User)</span>
                    <span className="text-xs text-un-onyx/60 mb-2 block">
                        Every client who redeems this code gets this many generations per month. Each generation produces 3 variants. Resets on the 1st. Blank = unlimited.
                    </span>
                    <input
                        type="number"
                        min="0"
                        value={form.monthly_script_limit}
                        onChange={(e) => setForm((f) => ({ ...f, monthly_script_limit: e.target.value }))}
                        className="un-input"
                        placeholder="e.g. 30 for one-a-day"
                        data-testid="code-monthly-limit-input"
                    />
                </label>
                <button
                    type="submit"
                    disabled={creating}
                    className="un-btn-primary flex items-center gap-2 justify-center md:col-span-2 self-end h-[46px]"
                    data-testid="create-code-button"
                >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Generate {parseInt(form.max_uses, 10) > 1 ? `Batch Code (${form.max_uses} Uses)` : "Single-Use Code"}
                </button>
            </form>

            {tags.length > 0 && (
                <div className="flex items-center gap-3 mb-4">
                    <Filter className="w-4 h-4 text-un-onyx/60" />
                    <span className="un-label text-un-onyx text-xs">Filter by Tag</span>
                    <select
                        value={tagFilter}
                        onChange={(e) => setTagFilter(e.target.value)}
                        className="un-input !py-1 !px-2 text-sm max-w-xs"
                        data-testid="codes-tag-filter"
                    >
                        <option value="">All Tags</option>
                        {tags.map((t) => (
                            <option key={t.id} value={t.name}>
                                {t.name} ({t.code_count})
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {loading ? (
                <div className="un-card p-10 flex items-center gap-3 justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-un-berry" />
                </div>
            ) : filteredCodes.length === 0 ? (
                <div className="un-card p-10 text-center">
                    <div className="un-h3 mb-1">No Codes Yet</div>
                    <div className="text-un-onyx/60 text-sm">Generate your first activation code above.</div>
                </div>
            ) : (
                <div className="border-2 border-un-onyx overflow-x-auto" data-testid="codes-table">
                    <table className="w-full text-left">
                        <thead className="bg-un-onyx text-white">
                            <tr>
                                {["Code", "Tag", "Usage", "Status", "Expires", ""].map((h) => (
                                    <th key={h} className="un-label px-4 py-3 border-r-2 border-white/20 last:border-r-0">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCodes.map((c) => {
                                const dl = daysLeft(c.expires_at);
                                const dlColor = dl == null ? "text-un-onyx/60" : dl < 0 ? "text-red-600" : dl <= 7 ? "text-un-berry" : dl <= 30 ? "text-orange-500" : "text-un-onyx/60";
                                const max = c.max_uses || 1;
                                const used = c.use_count || 0;
                                const pct = Math.min(100, (used / max) * 100);
                                return (
                                    <tr key={c.id} className="border-t-2 border-un-onyx" data-testid={`code-row-${c.id}`}>
                                        <td className="px-4 py-4 un-mono font-bold whitespace-nowrap">
                                            {c.code}
                                            {c.label && <div className="text-xs text-un-onyx/60 font-normal mt-1">{c.label}</div>}
                                        </td>
                                        <td className="px-4 py-4 text-sm">
                                            {c.tag ? (
                                                <span className="un-mono text-xs bg-un-water/30 border-2 border-un-onyx px-2 py-1 inline-block">
                                                    {c.tag}
                                                </span>
                                            ) : (
                                                <span className="text-un-onyx/40">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="un-mono text-xs font-bold mb-1">{used} / {max} used</div>
                                            <div className="w-32 h-2 border border-un-onyx bg-white">
                                                <div className="h-full bg-un-berry" style={{ width: `${pct}%` }} />
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`un-pill ${c.status === "unused" ? "un-pill-berry" : c.status === "used" ? "un-pill-onyx" : "un-pill-outline"}`}>
                                                {c.status === "used" ? "locked" : c.status}
                                            </span>
                                        </td>
                                        <td className={`px-4 py-4 un-mono text-xs whitespace-nowrap ${dlColor}`}>
                                            {c.expires_at ? (
                                                <div>
                                                    <div>{new Date(c.expires_at).toLocaleDateString()}</div>
                                                    <div className="text-[10px] font-bold">
                                                        {dl < 0 ? "expired" : `${dl}d left`}
                                                    </div>
                                                </div>
                                            ) : "—"}
                                        </td>
                                        <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                                            <button onClick={() => copyCode(c.code)} className="un-btn-ghost !px-2 inline-flex items-center gap-1 text-xs" data-testid={`code-copy-${c.id}`}>
                                                <Copy className="w-3 h-3" /> Copy
                                            </button>
                                            {c.status !== "revoked" && (
                                                <button onClick={() => revoke(c.id)} className="un-btn-outline text-xs" data-testid={`code-revoke-${c.id}`}>Revoke</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {addClientOpen && <AddClientDialog onClose={() => setAddClientOpen(false)} onCreated={load} availableTags={tags} />}
        </div>
    );
};

export default AdminCodes;
